import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  MenuItem, 
  useTheme, 
  Alert,
  Grid,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import { whatsAppHref } from '../constants/conversion';

const RequestQuote = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '', 
    company: '', 
    email: '', 
    phone: '', 
    projectType: '', 
    budget: '', 
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  
  // THESE ARE NOW LOADED FROM API INSTEAD OF BEING HARDCODED
  const [projectTypes, setProjectTypes] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState([]);

  // Load configuration on component mount
useEffect(() => {
  const loadConfig = async () => {
    try {
      const resp = await apiService.getPublicConfig();
      if (resp.success) {
        // map to strings used by your form (title / label)
        const types = (resp.projectTypes || []).map(t => t.title);
        const ranges = (resp.budgetRanges || []).map(r => r.label);
        setProjectTypes(types);
        setBudgetRanges(ranges);
      } else {
        setProjectTypes([]);
        setBudgetRanges([]);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setProjectTypes([]);
      setBudgetRanges([]);
    } finally {
      setConfigLoading(false);
    }
  };

  loadConfig();
}, []);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that config is loaded
    if (projectTypes.length === 0 || budgetRanges.length === 0) {
      setAlert({ 
        show: true, 
        type: 'error', 
        message: 'Form configuration not loaded. Please refresh the page.' 
      });
      return;
    }

    setLoading(true);
    setAlert({ show: false, type: '', message: '' });

    try {
      console.log('📤 Submitting quote data:', formData);
      
      const response = await apiService.submitQuote(formData);
      console.log('✅ Quote submission response:', response);
      
      setAlert({ 
        show: true, 
        type: 'success', 
        message: response.message || 'Quote request submitted successfully! We will contact you within 24 hours.' 
      });
      
      // Reset form
      setFormData({
        name: '', 
        company: '', 
        email: '', 
        phone: '', 
        projectType: '', 
        budget: '', 
        description: ''
      });
      
    } catch (error) {
      console.error('❌ Error submitting quote:', error);
      setAlert({ 
        show: true, 
        type: 'error', 
        message: error.response?.data?.message || 'Failed to submit quote request. Please try again or contact us directly.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Show loading while config is being fetched
  if (configLoading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      py: 8,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <Container maxWidth="md">
        <motion.div 
          initial={{ opacity: 0, y: 50 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h2" 
            textAlign="center" 
            gutterBottom 
            sx={{ 
              color: theme.palette.primary.main,
              fontWeight: 700,
              mb: 2
            }}
          >
            Get your free project scope
          </Typography>
          <Typography 
            variant="h5" 
            textAlign="center" 
            sx={{ 
              mb: 2, 
              color: 'text.secondary',
              maxWidth: 640,
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            One form is enough to start. We reply with questions, a rough range, and a call slot — software, AI,
            automation, or campaign systems for Kenya and East Africa.
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 4, color: 'text.secondary' }}>
            Prefer WhatsApp?{' '}
            <Box
              component="a"
              href={whatsAppHref('Hi Boldcomp — I want a project scope.')}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 700, color: 'secondary.dark' }}
            >
              Message us here
            </Box>{' '}
            (opens WhatsApp).
          </Typography>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.2 }}
        >
          <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                {alert.show && (
                  <Alert 
                    severity={alert.type} 
                    sx={{ mb: 3 }}
                    onClose={() => setAlert({ ...alert, show: false })}
                  >
                    {alert.message}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      required 
                      fullWidth 
                      label="Your Name" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="Company" 
                      name="company" 
                      value={formData.company} 
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      required 
                      fullWidth 
                      label="Email" 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      required 
                      fullWidth 
                      label="Phone" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      required 
                      fullWidth 
                      select 
                      label="Project Type" 
                      name="projectType" 
                      value={formData.projectType} 
                      onChange={handleChange}
                      disabled={loading || projectTypes.length === 0}
                    >
                      {projectTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      required 
                      fullWidth 
                      select 
                      label="Budget Range" 
                      name="budget" 
                      value={formData.budget} 
                      onChange={handleChange}
                      disabled={loading || budgetRanges.length === 0}
                    >
                      {budgetRanges.map((range) => (
                        <MenuItem key={range} value={range}>{range}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField 
                      required 
                      fullWidth 
                      label="Project Description" 
                      name="description" 
                      multiline 
                      rows={6} 
                      value={formData.description} 
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="Please describe your project in detail. Include your goals, target audience, timeline, and any specific requirements..."
                    />
                  </Grid>
                </Grid>

                <Button 
                  type="submit" 
                  variant="contained" 
                  color="secondary" 
                  size="large" 
                  fullWidth 
                  disabled={loading || projectTypes.length === 0 || budgetRanges.length === 0}
                  sx={{ 
                    mt: 4, 
                    py: 1.5,
                    borderRadius: '25px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(31, 182, 255, 0.3)',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} sx={{ color: 'white', mr: 2 }} />
                      Submitting...
                    </>
                  ) : (
                    'Request Quote'
                  )}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Quick response
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  We reply to every request within 24 hours.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Clear proposal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You get a straightforward quote and timeline.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box textAlign="center" sx={{ p: 2 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  No obligation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Request a quote with no commitment to proceed.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default RequestQuote;